import {inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {VaultsService as ApiVaultsService} from '../../api/services/vaults.service';
import {DialogService} from '../dialog/dialog.service';
import {SnackbarService} from '../snackbar/snackbar.service';
import {MagnetsService as ApiMagnetsService} from '../../api/services/magnets.service';
import {VaultQuery} from '../../queries/vault.query';
import {HistoryService} from '../history/history.service';
import {HttpContext, HttpErrorResponse} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {AuthService} from '../auth/auth.service';
import {MagnetService as BfaMagnetService, VaultService as BfaVaultService} from 'src/app/bfa-api/services';
import {Magnet as BfaMagnet, MagnetList} from 'src/app/bfa-api/models';
import {AUTH_TOKEN_HTTP_CONTEXT} from '../../constants/auth-token-http-context.constant';
import {SentryService} from '../sentry/sentry.service';
import {ApiErrorCodeEnum} from '../../enums/api-error-code.enum';
import {ApiErrorMappingService} from '../mapping/api-error-mapping/api-error-mapping.service';

@Injectable({
    providedIn: 'root'
})
export class MagnetService {
    private readonly selectedMagnetSignal: WritableSignal<BfaMagnet | undefined> = signal(undefined);
    private readonly apiErrorMappingService = inject(ApiErrorMappingService);
    private readonly apiMagnetsService = inject(ApiMagnetsService);
    private readonly apiVaultsService = inject(ApiVaultsService);
    private readonly authService = inject(AuthService);
    private readonly bfaMagnetService = inject(BfaMagnetService);
    private readonly bfaVaultService = inject(BfaVaultService);
    private readonly dialogService = inject(DialogService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly historyService = inject(HistoryService);
    private readonly sentryService = inject(SentryService);
    private readonly vaultQuery = inject(VaultQuery);

    public setSelectedMagnet(magnet?: BfaMagnet): void {
        this.selectedMagnetSignal.set(magnet ?? undefined);
    }

    public getSelectedMagnetId(): string | undefined {
        return this.selectedMagnetSignal()?.magnetId;
    }

    public getSelectedMagnet(): BfaMagnet | undefined {
        return this.selectedMagnetSignal();
    }

    public getSelectedMagnetSignal(): Signal<BfaMagnet | undefined> {
        return this.selectedMagnetSignal.asReadonly();
    }

    public async createMagnet(magnetName: string): Promise<boolean> {
        const vaultId = this.vaultQuery.getFirstActiveId();
        if (!vaultId) {
            this.snackbarService.showError('MAGNETS_CREATE_ERROR_MSG');
            this.sentryService.captureException(new Error('Cannot create magnet as active vault could not be determined.'));
            return false;
        }

        const bearer = await this.authService.getBearer();
        try {
            await firstValueFrom(this.apiVaultsService.VaultsCreateMagnetResponse({
                vaultId,
                magnetCreationData: { name: magnetName },
                Authorization: bearer
            }));
        } catch (error) {
            if (error instanceof HttpErrorResponse) {
                switch (error.status) {
                    case 403:
                        this.snackbarService.showError('MAGNETS_CREATE_ERROR_MSG_PERMISSION', error);
                        return false;
                    case 400:
                        this.snackbarService.showError((error.error?.code === ApiErrorCodeEnum.ReturnedExisting) ? 'MAGNETS_CREATE_ERROR_MSG_SAME_NAME' : 'MAGNETS_CREATE_ERROR_MSG', error);
                        return false;
                }
            }

            this.snackbarService.showError('MAGNETS_CREATE_ERROR_MSG', error as Error);
            return false;
        }

        return true;
    }

    public async createSubMagnet(magnetName: string, magnetId: string): Promise<boolean> {
        const bearer = await this.authService.getBearer();
        try {
            await firstValueFrom(this.apiMagnetsService.MagnetsCreateMagnet({
                magnetId,
                magnetCreationData: { name: magnetName },
                Authorization: bearer
            }));
        } catch (error) {
            if (error instanceof HttpErrorResponse && error.status) {
                switch (error.status) {
                    case 403:
                        this.snackbarService.showError('MAGNETS_CREATE_ERROR_MSG_PERMISSION', error);
                        return false;
                    case 400:
                        this.snackbarService.showError((error.error?.code === ApiErrorCodeEnum.ReturnedExisting) ? 'MAGNETS_CREATE_ERROR_MSG_SAME_NAME' : 'MAGNETS_CREATE_ERROR_MSG', error);
                        return false;
                }
            }

            this.snackbarService.showError('MAGNETS_CREATE_ERROR_MSG', error as Error);
            return false;
        }

        return true;
    }

    // No expected BackendForAppErrorCodes
    public async fetchTopLevelMagnetsForVault(vaultId: string): Promise<MagnetList> {
        const token = await this.authService.getToken();
        try {
            return await firstValueFrom((this.bfaVaultService.vaultGetMagnetList({ vaultId }, new HttpContext().set(AUTH_TOKEN_HTTP_CONTEXT, token))));
        } catch (error) {
            throw this.apiErrorMappingService.mapBfaError(error);
        }
    }

    // Possible BackendForAppErrorCodes: NotAuthorized, MagnetNotFound
    public async fetchAndGetMagnet(magnetId: string): Promise<BfaMagnet> {
        const token = await this.authService.getToken();
        try {
            return await firstValueFrom((this.bfaMagnetService.magnetGetMagnet({ magnetId }, new HttpContext().set(AUTH_TOKEN_HTTP_CONTEXT, token))));
        } catch (error) {
            throw this.apiErrorMappingService.mapBfaError(error);
        }
    }

    public async deleteMagnet(magnetId: string, magnetIsFolder: boolean): Promise<boolean> {
        const deletionConfirmed = await this.dialogService.showConfirmDialog({
            messageTranslationKey: magnetIsFolder ? 'FOLDER_DELETE_CONFIRM_MESSAGE' : 'MAGNET_DELETE_CONFIRM_MESSAGE',
            titleTranslationKey: magnetIsFolder ? 'FOLDER_DELETE_CONFIRM_TITLE' : 'MAGNET_DELETE_CONFIRM_TITLE',
            actionTranslationKey: 'BUTTON.DELETE',
        }, 'delete-magnet');
        
        if (!deletionConfirmed) {
            return false;
        }

        const bearer = await this.authService.getBearer();
        try {
            await firstValueFrom(this.apiMagnetsService.MagnetsDelete({
                magnetId: magnetId,
                Authorization: bearer
            }));

            this.historyService.removeHistoryItemsWithPathContaining(magnetId);
        } catch (error) {
            if (error instanceof HttpErrorResponse && error.status === 403) {
                this.snackbarService.showError('ERROR.MAGNET_DELETE_PERMISSION_MSG', error);
                return false;
            }

            this.snackbarService.showError('ERROR.MAGNET_DELETE_MSG', error as Error);
            return false;
        }

        return true;
    }
}
